import { FileSourceEnum } from '@systemeio/file-manager'
import {
  BreadcrumbsData,
  Checkbox,
  FormInput,
  FormSelect,
  Hint,
  LocaleEnum,
  Popover,
  PrimaryButton,
  getLocaleLanguage,
  toast,
} from '@systemeio/ui'
import { BadRequest } from '@systemeio/util'
import { CoursesLoco } from 'modules/courses/constants/courses-loco'
import {
  COURSES_API,
  disableReminder,
  enableReminder,
  updateCourse,
} from 'modules/courses/courses/api/courses-api'
import { getFunnelSteps } from 'modules/courses/courses/api/sales-funnels-api'
import CourseSkeleton from 'modules/courses/courses/components/course-skeleton'
import ReminderEmailModal from 'modules/courses/courses/components/reminder-email-modal'
import ReminderSettingsModal from 'modules/courses/courses/components/reminder-settings-modal'
import { useCourse } from 'modules/courses/courses/hooks/use-course'
import { useReminder } from 'modules/courses/courses/hooks/use-reminder'
import { useSalesFunnels } from 'modules/courses/courses/hooks/use-sales-funnels'
import {
  CourseApiInterface,
  CourseEditApiInterface,
  CourseEditInterface,
  CourseErrorsInterface,
  CourseId,
  CourseResponseErrorsInterface,
} from 'modules/courses/courses/types/course-interface'
import { SalesFunnelInterface } from 'modules/courses/courses/types/sales-funnel-interface'
import { fontsList } from 'modules/courses/courses/utils/font-utils'
import { getCourseThemesData } from 'modules/courses/courses/utils/theme-utils'
import CertificationSettings from 'modules/profile/course-settings/components/certification-settings'
import { NextPage } from 'next'
import { useRouter } from 'next/router'
import React, { useEffect, useMemo, useState } from 'react'
import BreadcrumbsDashboard from 'shared/components/breadcrumbs'
import CodeEditor from 'shared/components/code-editor'
import ConfirmationModal from 'shared/components/confirmation-modal'
import { ImageUploaderWithPreviewImage } from 'shared/components/file-uploader/image-uploader-with-preview-image'
import FormTextarea from 'shared/components/form-textarea'
import FormUrlPathInput from 'shared/components/form-url-path-input'
import { FieldLabel } from 'shared/components/form/field-label'
import { RequestModuleEnum } from 'shared/enums/request-module-enum'
import { TLocoType, useLocoTranslation } from 'shared/hooks/use-loco-translation'
import { useMutationUpdate } from 'shared/hooks/use-mutation-update'
import { useRevalidateModules } from 'shared/hooks/use-revalidate-modules'
import { useUpdateData } from 'shared/hooks/use-update-data'
import useUserDomains from 'shared/hooks/use-user-domains'
import { DataFile } from 'shared/types/datafile-type'
import { LocaleDataInterface } from 'shared/types/locale-data-interface'
import { FunnelStepInterface } from 'shared/types/payment-interface'
import { UserDomainInterface } from 'shared/types/user-domain-interface'
import { getBaseServerSideProps } from 'shared/utils/get-base-server-side-props'
import { KeyedMutator } from 'swr'
import { QuestionMarkIcon, SaveIcon } from '@systemeio/ui/icons'

const defaultErrors: CourseErrorsInterface = {
  fields: {
    name: '',
    locale: '',
    domain: '',
    path: '',
    fontFamily: '',
    theme: '',
    teacherPhoto: '',
  },
  common: [],
}

const getSelectDataCaptionFromName = (data: UserDomainInterface[] | SalesFunnelInterface[]) => {
  return data.map(instance => ({
    id: instance.id,
    caption: instance.name,
  }))
}

const getDefaultSalesCoursesSelect = (t: TLocoType) => {
  return {
    id: 'default',
    caption: t('dashboard.course.funnel_default_title'),
  }
}

const getFontData = () => {
  return fontsList.map(fontFamily => ({
    id: fontFamily,
    caption: fontFamily,
  }))
}

const CourseEdit: NextPage = () => {
  const router = useRouter()
  const courseId = Number(router.query.courseId) as CourseId
  const { userDomains } = useUserDomains()
  const { courseData, mutate } = useCourse(courseId)
  const { data: salesFunnelsData } = useSalesFunnels()

  return (
    <>
      {courseData && userDomains && salesFunnelsData ? (
        <CourseEditForm
          courseId={Number(courseId)}
          userDomains={userDomains}
          courseData={courseData}
          salesFunnelsData={salesFunnelsData}
          mutateCourse={mutate}
        />
      ) : (
        <CourseSkeleton />
      )}
    </>
  )
}

interface CourseEditFormProps {
  courseId: number
  userDomains: UserDomainInterface[]
  courseData: CourseEditApiInterface
  salesFunnelsData: SalesFunnelInterface[]
  mutateCourse: KeyedMutator<CourseEditApiInterface>
}

const CourseEditForm = ({
  courseId,
  userDomains,
  courseData,
  salesFunnelsData,
  mutateCourse,
}: CourseEditFormProps) => {
  const { t } = useLocoTranslation()
  const [tempState, setTempState] = useState<CourseEditInterface>({
    description: courseData.description || '',
    domain: courseData.domainId,
    embeddedCode: courseData.embeddedCode || '',
    fontFamily: courseData.fontFamily,
    image: courseData.image?.id,
    teacherPhoto: courseData.teacherPhoto?.id,
    instructorName: courseData.instructorName,
    locale: courseData.locale,
    logo: courseData.logo?.id,
    name: courseData.name,
    path: courseData.path,
    salesFunnelStep: courseData.salesFunnelStepId,
    showModulesTitles: courseData.showModulesTitles,
    theme: courseData.themeId,
    issueCertificateUponCompletion: courseData.issueCertificateUponCompletion,
    certificateLogo: courseData.certificateLogo?.id,
    signature: courseData.signature?.id,
  })
  const [activeFunnel, setActiveFunnel] = useState<number | null | string>(courseData.salesFunnelId)
  const [courseLogoImage, setCourseLogoImage] = useState(courseData.logo?.path)
  const [courseImage, setCourseImage] = useState(courseData.image?.path)
  const [courseTeacherPhoto, setCourseTeacherPhoto] = useState(courseData.teacherPhoto?.path)
  const [isFetching, setIsFetching] = useState(false)
  const [errors, setErrors] = useState<CourseErrorsInterface>(defaultErrors)
  const [funnelStepsCache, setFunnelStepsCache] = useState<Record<number, FunnelStepInterface[]>>(
    {},
  )
  const [changeDomainConfirmationIsShown, setChangeDomainConfirmationIsShown] = useState(false)
  const [confirmationDomain, setConfirmationDomain] = useState<number | null>(null)
  const [isReminderEnabled, setIsReminderEnabled] = useState<boolean>(false)

  const [isOpenReminderSettingsModal, setIsOpenReminderSettingsModal] = useState(false)
  const [isOpenReminderEmailModal, setIsOpenReminderEmailModal] = useState(false)

  const [opened, setOpened] = useState(false)
  const breadcrumbsData: BreadcrumbsData[] = [
    { caption: t('dashboard.courses.title_plural'), href: '/courses', isStatic: true },
    { caption: courseData?.name || '' },
  ]

  const localeData: LocaleDataInterface[] = Object.values(LocaleEnum).map(locale => ({
    id: locale as LocaleEnum,
    caption: getLocaleLanguage(locale),
  }))

  const updateData = useUpdateData({
    api: COURSES_API,
    constantApi: [
      salesFunnelsData,
      userDomains,
      `${COURSES_API}/list`,
      `${COURSES_API}/${courseId}/reminder-settings`,
      `${COURSES_API}/${courseId}/reminder-settings/email`,
    ],
  })
  const mutationUpdate = useMutationUpdate()
  const { revalidateModules } = useRevalidateModules()

  const {
    reminderData,
    reminderEmailData,
    isReminderEmailSettingsLoading,
    isReminderDataLoading,
    mutateReminderEmailData,
    mutateReminderData,
  } = useReminder({
    courseId,
    isReminderFetchAllowed: courseData?.hasReminderSettings,
    isReminderEmailFetchAllowed: courseData?.hasReminderEmailSettings,
  })

  const hasReminderSettings = useMemo(() => !!courseData?.hasReminderSettings, [courseData?.id])

  useEffect(() => {
    if (hasReminderSettings && reminderData) {
      setIsReminderEnabled(reminderData.course.emailRemindersEnabled)
    }
  }, [reminderData, hasReminderSettings])

  useEffect(() => {
    if (typeof activeFunnel == 'number') {
      if (activeFunnel !== courseData.salesFunnelId) {
        setTempState(prev => ({ ...prev, salesFunnelStep: null }))
      }
      const getFunnelStepsByFunnelId = async () => {
        if (!funnelStepsCache[activeFunnel]) {
          const { data } = await getFunnelSteps(activeFunnel)
          setFunnelStepsCache(prev => {
            return {
              ...prev,
              [activeFunnel]: data,
            }
          })
          return data
        }
      }
      getFunnelStepsByFunnelId()
    }
    if (activeFunnel === 'default') {
      setTempState(prev => ({ ...prev, salesFunnelStep: null }))
    }
  }, [activeFunnel])

  const handleSetUploadedLogo = (dataFile: DataFile) => {
    setTempState(prev => {
      return { ...prev, logo: dataFile.id }
    })
    setCourseLogoImage(dataFile.path)
  }
  const handleSetUploadedTeacherPhoto = (dataFile: DataFile) => {
    setTempState(prev => {
      return { ...prev, teacherPhoto: dataFile.id }
    })
    setCourseTeacherPhoto(dataFile.path)
  }
  const handleSetUploadedImage = (dataFile: DataFile) => {
    setTempState(prev => {
      return { ...prev, image: dataFile.id }
    })
    setCourseImage(dataFile.path)
  }
  const onSave = async () => {
    try {
      setErrors(defaultErrors)
      setIsFetching(true)

      const response = await updateCourse(tempState, courseId)
      await updateData(
        false,
        undefined,
        {
          mutationData: courseData,
          onMutate: async () => {
            await mutateCourse(
              () => ({
                ...response.data,
              }),
              false,
            )
          },
        },
        {
          id: courseData?.id,
          onMutate: async apiKey => {
            await mutationUpdate<CourseApiInterface>(
              apiKey,
              data =>
                data && {
                  hasMore: data.hasMore,
                  items: data.items.map(item => {
                    if (item.id === response.data.id) item.name = response.data.name
                    return item
                  }),
                },
            )
          },
        },
      )

      try {
        if (isReminderEnabled) {
          await enableReminder(courseId)
        } else if (hasReminderSettings) {
          await disableReminder(courseId)
        }
      } catch (e) {
        if (e instanceof BadRequest) {
          toast.error(e.errors.common.join(''))
        }
      } finally {
        setIsFetching(false)
      }
      await revalidateModules([RequestModuleEnum.MembershipCourse])
      toast.success(t('global.changes_saved'))
    } catch (e) {
      setIsFetching(false)
      if (e instanceof BadRequest) {
        const courseEditErrors = e.errors as unknown as CourseResponseErrorsInterface
        setErrors(prev => ({
          ...prev,
          fields: {
            name: courseEditErrors.fields.name?.join(''),
            locale: courseEditErrors.fields.locale?.join(''),
            domain: courseEditErrors.fields.domain?.join(''),
            path: courseEditErrors.fields.path?.join(''),
            fontFamily: courseEditErrors.fields.fontFamily?.join(''),
            theme: courseEditErrors.fields.theme?.join(''),
            teacherPhoto: courseEditErrors.fields.teacherPhoto?.join(''),
          },
        }))
        if (e.errors.common) {
          const commonErrors = e.errors.common
          setErrors(prev => {
            return {
              ...prev,
              common: commonErrors,
            }
          })
        }
      } else {
        toast.error(t('global.error'))
      }
    }
  }

  const handleChangeEmbeddedCode = (value: string) => {
    setTempState(prev => {
      return { ...prev, embeddedCode: value }
    })
  }

  const handleChangeReminder = (value: boolean) => setIsReminderEnabled(value)

  useEffect(() => {
    if (confirmationDomain) {
      setOpened(true)
    }
  }, [confirmationDomain])

  const [isCodeValid, setIsCodeValid] = useState(true)

  return (
    <>
      <div className={`flex flex-col gap-10`}>
        <div className="flex items-center justify-between gap-5">
          <BreadcrumbsDashboard data={breadcrumbsData} isFetching={!courseData} />
          <PrimaryButton
            onClick={onSave}
            isFetching={isFetching}
            disabled={!courseData || !isCodeValid}
          >
            <SaveIcon />
            <span>{t('global.save')}</span>
          </PrimaryButton>
        </div>
        <>
          {courseData && userDomains && salesFunnelsData ? (
            <div className="bg-background-secondary flex flex-col gap-7 rounded-sm p-6 px-5 lg:gap-7">
              <div className="[&>*]:flex-1 flex flex-col justify-between gap-7 lg:flex-row lg:gap-10">
                <FormInput
                  value={tempState.name}
                  onChange={e => setTempState(prev => ({ ...prev, name: e.target.value }))}
                  label={t('dashboard.courses.course_title')}
                  required
                  error={errors.fields.name}
                  className={'flex-auto'}
                />
                <FormInput
                  label={t('dashboard.course.instructor_name_title')}
                  value={tempState.instructorName || ''}
                  onChange={e =>
                    setTempState(prev => ({ ...prev, instructorName: e.target.value }))
                  }
                  className={'flex-auto'}
                />
                <FormSelect
                  data={localeData}
                  label={t('dashboard.course.language_title')}
                  className={'sm:min-w-0'}
                  required
                  value={tempState.locale}
                  onChange={value => value && setTempState(prev => ({ ...prev, locale: value }))}
                  withoutCloseIcon
                  error={errors.fields.locale}
                />
              </div>
              <FormTextarea
                value={tempState.description || undefined}
                onChange={e => setTempState(prev => ({ ...prev, description: e.target.value }))}
                label={
                  <>
                    <div className="[&>svg]:fill-brand-primary [&>svg]:main-transition-colors flex  items-center gap-1.5">
                      {t('dashboard.course.description_title')}
                      <Popover
                        label={t('dashboard.course.description_popover_title')}
                        popoverClassName={'w-[200px] sm:w-[350px]'}
                      >
                        {show => (
                          <QuestionMarkIcon
                            className={`${
                              show ? 'fill-brand-primary' : 'fill-fill-primary'
                            } group-focus-visible:fill-brand-primary h-[16px] w-[16px]`}
                          />
                        )}
                      </Popover>
                    </div>
                  </>
                }
              />
              <div className="[&>*]:flex-1 flex flex-col justify-between gap-7 lg:flex-row lg:gap-10">
                <div className="flex flex-col gap-1.5">
                  <FormSelect
                    data={getSelectDataCaptionFromName(userDomains)}
                    label={t('dashboard.course.course_domain_title')}
                    className={'sm:min-w-0'}
                    required
                    value={tempState.domain || undefined}
                    onChange={value => {
                      if (!value) return
                      if (tempState.domain && !changeDomainConfirmationIsShown) {
                        setConfirmationDomain(value)
                      } else {
                        setTempState(prev => ({ ...prev, domain: value }))
                      }
                    }}
                    error={errors.fields.domain}
                    withoutCloseIcon
                  />
                  <ConfirmationModal
                    className={'z-50'}
                    title={t('dashboard.course.course_domain_confirm_title')}
                    confirmationText={t('dashboard.course.course_domain_confirmation_text_title')}
                    opened={opened}
                    onClose={() => setOpened(false)}
                    onConfirm={async () => {
                      if (confirmationDomain) {
                        setChangeDomainConfirmationIsShown(true)
                        setTempState(prev => ({ ...prev, domain: confirmationDomain }))
                      }
                    }}
                  />
                </div>
                <FormUrlPathInput
                  value={tempState.path}
                  onChange={e => setTempState(prev => ({ ...prev, path: e.target.value }))}
                  label={
                    <>
                      <div className="[&>svg]:fill-brand-primary [&>svg]:main-transition-colors flex  items-center gap-1.5">
                        {t('global.url_path')}
                        <Popover
                          label={t('dashboard.funnel.configuration.webinar.url_path_popover-2')}
                          popoverClassName={'w-[200px] sm:w-[350px]'}
                        >
                          {show => (
                            <QuestionMarkIcon
                              className={`${
                                show ? 'fill-brand-primary' : 'fill-fill-primary'
                              } group-focus-visible:fill-brand-primary h-[16px] w-[16px]`}
                            />
                          )}
                        </Popover>
                      </div>
                    </>
                  }
                  required
                  domainName={userDomains.find(el => el.id === tempState.domain)?.name}
                  additionalPath="school/course"
                  error={errors.fields.path}
                />
              </div>
              <div className="[&>*]:flex-1 flex flex-col justify-between gap-7 lg:flex-row lg:gap-10">
                <FormSelect
                  data={getCourseThemesData(t)}
                  label={t('dashboard.course.theme_title')}
                  className={'sm:min-w-0'}
                  value={tempState.theme}
                  onChange={value => value && setTempState(prev => ({ ...prev, theme: value }))}
                  error={errors.fields.theme}
                  withoutCloseIcon
                />
                <FormSelect
                  data={getFontData()}
                  label={t('dashboard.course.font_title')}
                  className={'sm:min-w-0'}
                  required
                  value={tempState.fontFamily}
                  onChange={value =>
                    value && setTempState(prev => ({ ...prev, fontFamily: value }))
                  }
                  error={errors.fields.fontFamily}
                  withoutCloseIcon
                />
              </div>
              <div className="[&>*]:flex-1 flex flex-col justify-between gap-7 lg:flex-row lg:gap-10">
                <ImageUploaderWithPreviewImage
                  source={FileSourceEnum.Membership}
                  label={
                    <Hint label={t('dashboard.settings.membership.teacher_photo_popover_title')}>
                      {t('dashboard.settings.membership.teacher_photo_title')}
                    </Hint>
                  }
                  error={errors.fields.teacherPhoto}
                  value={courseTeacherPhoto ? { path: courseTeacherPhoto } : null}
                  previewClassName="h-[200px] w-[200px]"
                  onRemove={() => {
                    setTempState(prev => {
                      return {
                        ...prev,
                        teacherPhoto: undefined,
                      }
                    })
                    setCourseTeacherPhoto('')
                  }}
                  aspectRatio={1}
                  onSelect={handleSetUploadedTeacherPhoto}
                />
                <ImageUploaderWithPreviewImage
                  label={
                    <Hint label={t('dashboard.course.logo_popover_title')}>
                      {t('dashboard.course.logo_title')}
                    </Hint>
                  }
                  value={courseLogoImage ? { path: courseLogoImage } : null}
                  source={FileSourceEnum.Membership}
                  previewClassName="w-[364px] h-[200px] !object-contain object-top"
                  onRemove={() => {
                    setTempState(prev => {
                      return {
                        ...prev,
                        logo: undefined,
                      }
                    })
                    setCourseLogoImage('')
                  }}
                  onSelect={handleSetUploadedLogo}
                />
                <ImageUploaderWithPreviewImage
                  label={
                    <Hint label={t('dashboard.course.image_popover_title')}>
                      {t('dashboard.course.image_title')}
                    </Hint>
                  }
                  aspectRatio={16 / 9}
                  value={courseImage ? { path: courseImage } : null}
                  source={FileSourceEnum.Membership}
                  previewClassName="h-[200px] w-[364px]"
                  onRemove={() => {
                    setTempState(prev => {
                      return {
                        ...prev,
                        image: undefined,
                      }
                    })
                    setCourseImage('')
                  }}
                  onSelect={handleSetUploadedImage}
                />
              </div>
              <div className="[&>*]:flex-1 flex flex-col justify-between gap-7 lg:flex-row lg:gap-10">
                <FormSelect
                  data={[
                    getDefaultSalesCoursesSelect(t),
                    ...getSelectDataCaptionFromName(salesFunnelsData),
                  ]}
                  label={
                    <>
                      <div
                        className={`[&>svg]:fill-brand-primary [&>svg]:main-transition-colors flex items-center gap-3`}
                      >
                        {t('dashboard.course.sales_page_title')}
                        <Popover
                          label={t('dashboard.course.sales_page_popover_title')}
                          popoverClassName={'w-[200px] sm:w-[350px]'}
                        >
                          {show => (
                            <QuestionMarkIcon
                              className={`${
                                show ? 'fill-brand-primary' : 'fill-fill-primary'
                              } group-focus-visible:fill-brand-primary h-[16px] w-[16px]`}
                            />
                          )}
                        </Popover>
                      </div>
                    </>
                  }
                  className={'sm:min-w-0'}
                  value={activeFunnel || getDefaultSalesCoursesSelect(t).id}
                  onChange={value => value && setActiveFunnel(value)}
                  withoutCloseIcon
                />
                {activeFunnel && typeof activeFunnel === 'number' ? (
                  <FormSelect
                    className={'align-bottom sm:min-w-0'}
                    data={
                      funnelStepsCache[activeFunnel]
                        ? getSelectDataCaptionFromName(funnelStepsCache[activeFunnel])
                        : undefined
                    }
                    label={t('dashboard.course.funnel_step_title')}
                    value={tempState.salesFunnelStep || undefined}
                    onChange={value =>
                      value && setTempState(prev => ({ ...prev, salesFunnelStep: value }))
                    }
                    withoutCloseIcon
                  />
                ) : (
                  <div />
                )}
              </div>
              <div className="flex flex-wrap gap-10">
                <Checkbox
                  value={tempState.showModulesTitles}
                  label={t('dashboard.course.show_module_titles_title')}
                  onChange={() =>
                    setTempState(prev => {
                      return { ...prev, showModulesTitles: !prev.showModulesTitles }
                    })
                  }
                />
              </div>
              <div className="flex flex-col gap-4">
                <div className="flex flex-col gap-1.5">
                  <FieldLabel
                    labelClassName={'text-sm font-medium'}
                    label={t('dashboard.course.reminder.show_reminder_title')}
                  />
                  <Checkbox
                    disabled={isReminderDataLoading || isReminderEmailSettingsLoading}
                    value={isReminderEnabled}
                    onChange={handleChangeReminder}
                    label={t('dashboard.course.reminder.show_reminder_label')}
                  />
                </div>
                {isReminderEnabled && (
                  <div className="flex gap-5">
                    <PrimaryButton onClick={() => setIsOpenReminderSettingsModal(true)}>
                      {t('dashboard.course.reminder.settings.title')}
                    </PrimaryButton>
                    <PrimaryButton onClick={() => setIsOpenReminderEmailModal(true)}>
                      {t('dashboard.course.reminder.email_edit.title')}
                    </PrimaryButton>
                  </div>
                )}
              </div>
              <div className="flex flex-col gap-1.5">
                <CodeEditor
                  value={tempState.embeddedCode}
                  onChange={handleChangeEmbeddedCode}
                  setIsValid={setIsCodeValid}
                  label={
                    <Hint label={t('dashboard.course.embedded_code_popover_title')}>
                      {t('dashboard.course.embedded_code_title')}
                    </Hint>
                  }
                />
              </div>
              <CertificationSettings
                onChange={setTempState}
                tempEditState={tempState}
                courseData={courseData}
              />
              {errors.common && (
                <>
                  {errors.common.map((error, index) => (
                    <p className="text-red px-6 text-sm" key={index}>
                      {error}
                    </p>
                  ))}
                </>
              )}
              <ReminderSettingsModal
                data={reminderData}
                opened={isOpenReminderSettingsModal}
                courseId={courseId}
                onClose={() => setIsOpenReminderSettingsModal(false)}
                isDataLoading={isReminderDataLoading}
                mutateData={mutateReminderData}
                mutateCourse={mutateCourse}
                hasReminderEmailSettings={courseData.hasReminderEmailSettings}
              />
              <ReminderEmailModal
                data={reminderEmailData}
                opened={isOpenReminderEmailModal}
                courseId={courseId}
                onClose={() => setIsOpenReminderEmailModal(false)}
                isDataLoading={isReminderEmailSettingsLoading}
                mutateData={mutateReminderEmailData}
                mutateCourse={mutateCourse}
                hasReminderSettings={courseData.hasReminderSettings}
              />
            </div>
          ) : (
            <CourseSkeleton />
          )}
        </>
      </div>
    </>
  )
}

export const getServerSideProps = getBaseServerSideProps(CoursesLoco, 'header.top_menu.courses')

export default CourseEdit
